import { useState } from 'react';
import ReportService from '../../services/reports/Report.service';
import TipContext from '../../providers/GlobalTip';

export interface ReportRef {
    generatePdf: () => boolean;
    generateCsv?: () => boolean;
}

interface BaseReportProps {
    title: string;
    subtitle?: string;
    tableColumns: string[];
    tableData: Array<Array<string | number>>;
    fileName: string;
    orientation?: 'p' | 'l';
    pageSize?: string;
}

const BaseReport = ({
    title,
    subtitle,
    tableColumns,
    tableData,
    fileName,
    orientation = 'l',
    pageSize = 'a4',
}: BaseReportProps) => {
    const globalTip = TipContext.useMessage();
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingAction, setPendingAction] = useState<'pdf' | 'csv' | null>(null);

    const handleExport = (type: 'pdf' | 'csv') => {
        setPendingAction(type);
        setShowConfirm(true);
    };

    const handleConfirm = () => {
        setShowConfirm(false);
        if (pendingAction === 'pdf') {
            try {
                ReportService.tableToPdf({
                    columns: tableColumns,
                    data: tableData,
                    fileName,
                    title,
                    subtitle,
                    orientation,
                    pageSize
                });
            } catch (error) {
                if (globalTip) globalTip.show("Failed to generate PDF");
            }
        } else if (pendingAction === 'csv') {
            try {
                ReportService.tableToCSV({
                    columns: tableColumns,
                    data: tableData,
                    fileName
                });
            } catch (error) {
                if (globalTip) globalTip.show("Failed to export CSV");
            }
        }
        setPendingAction(null);
    };

    const handleCancel = () => {
        setShowConfirm(false);
        setPendingAction(null);
    };

    // Expose these handlers for use in parent components (e.g., ReportTable)
    (window as any).BaseReportExport = handleExport;

    return (
        <>
            {showConfirm && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white p-6 rounded shadow-lg flex flex-col items-center max-w-md">
                        <div className="mb-4 text-lg font-semibold text-center">
                            Export Report
                        </div>
                        <div className="mb-4 text-gray-700 text-sm text-center">
                            Generating your report may take up to a minute. You can continue browsing in another tab while the export is processing.<br/>
                            <span className="font-semibold">Note:</span> You must allow popups for the export to open in a new tab.
                        </div>
                        <div className="flex gap-4">
                            <button onClick={handleConfirm} className="px-4 py-2 bg-blue-600 text-white rounded">Continue</button>
                            <button onClick={handleCancel} className="px-4 py-2 bg-gray-300 text-gray-700 rounded">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BaseReport;